import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import PasswordIcon from '@mui/icons-material/Password';
import TranslateIcon from '@mui/icons-material/Translate';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import LogoutIcon from '@mui/icons-material/Logout';
import MobileActionsSheet from './MobileActionsSheet';
import ProfileDialog from '../../pages/dialogs/ProfileDialog';
import ChangePasswordDialog from '../../pages/dialogs/ChangePasswordDialog';
import { UsersHead } from '../../layouts/dashboard/header/AccountPopover';
import { useStateSelector } from '../../context/StateSelectors';
import { useColorMode } from '../../theme/ColorModeProvider';
import config from '../../config';
import helper from '../../utils/helper';

// The page's ⋮ menu on a phone: the page's own actions, then what the header
// holds on a wider screen — theme, language and the account menu — since a
// phone's header has room for the page title and two buttons only.
export default function MobilePageMenu({ open, onClose, pageItems }) {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const server = useStateSelector((state) => state.server);
    const userInfo = useStateSelector((state) => state.userInfo);
    const { mode, toggle: toggleColorMode } = useColorMode();
    const [profileOpen, setProfileOpen] = useState(false);
    const [passwordOpen, setPasswordOpen] = useState(false);

    const langs = config.langs || [];
    const langIndex = langs.findIndex(
        (lang) => lang.value === i18n.resolvedLanguage
    );
    const currentLang = langs[langIndex] || langs[0];
    const nextLang = langs[(Math.max(langIndex, 0) + 1) % langs.length];

    // The profile form edits only the fields it shows.
    const profileData = Object.fromEntries(
        Object.entries(userInfo || {}).filter(([key]) =>
            UsersHead.some((head) => head.id === key)
        )
    );

    const items = [
        ...pageItems,
        { key: 'accountSection', type: 'divider', label: t('mobile.account') },
        {
            key: 'home',
            label: t('profile.home'),
            icon: <HomeOutlinedIcon />,
            onClick: () => navigate('/app/dashboard')
        },
        {
            key: 'profile',
            label: t('profile.profile'),
            icon: <AccountCircleOutlinedIcon />,
            onClick: () => setProfileOpen(true)
        },
        {
            key: 'changePassword',
            label: t('text.changePassword'),
            icon: <PasswordIcon />,
            onClick: () => setPasswordOpen(true)
        },
        {
            key: 'colorMode',
            label: t(
                mode === 'dark' ? 'common.switchToLight' : 'common.switchToDark'
            ),
            icon:
                mode === 'dark' ? (
                    <LightModeOutlinedIcon />
                ) : (
                    <DarkModeOutlinedIcon />
                ),
            onClick: toggleColorMode
        },
        // Tapping moves on to the next language; with two, that is a toggle.
        langs.length > 1 && {
            key: 'language',
            label: `${t('mobile.language')}: ${currentLang.label}`,
            icon: <TranslateIcon />,
            onClick: () => helper.changeLanguage(nextLang.value)
        },
        {
            key: 'logout',
            label: t('profile.logout'),
            icon: <LogoutIcon />,
            color: 'error.main',
            onClick: () => {
                server.auth.logoutUser();
                navigate('/login', { replace: true });
            }
        }
    ].filter(Boolean);

    return (
        <>
            <MobileActionsSheet
                open={open}
                onClose={onClose}
                title={t('mobile.pageActions')}
                items={items}
            />
            <ProfileDialog
                dialogOpen={profileOpen}
                dialogData={profileData}
                Head={UsersHead}
                onSave={() => setProfileOpen(false)}
                onClose={() => setProfileOpen(false)}
            />
            <ChangePasswordDialog
                open={passwordOpen}
                onClose={() => setPasswordOpen(false)}
                userId={userInfo?.id}
                askCurrent
            />
        </>
    );
}

MobilePageMenu.propTypes = {
    onClose: PropTypes.func.isRequired,
    open: PropTypes.bool.isRequired,
    pageItems: PropTypes.array.isRequired
};
